import React, { useState, useEffect } from "react";
import { Table, Button, Select, Space, Typography, Popconfirm, Modal, List, Divider, message, Spin, Tag, Input, Avatar } from "antd";
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  FacebookOutlined,
  InstagramOutlined,
  LinkedinOutlined,
  LinkOutlined,
  CopyOutlined,
  TwitterOutlined,
  SendOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import AffiliateUpdateModal from "./AffiliateUpdateModal";
import TelegramIcon from "../../../components/TelegramIcon";

const AffiliateRequest = () => {
  const [affiliateList, setAffiliateList] = useState([]);
  const [loading, setLoading] = useState(false);
  //   const [currentLoadingId, setCurrentLoadingId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [currentAffiliateLabel, setCurrentAffiliateLabel] = useState("");
  const [isLinksModalVisible, setIsLinksModalVisible] = useState(false);
  const [currentLinks, setCurrentLinks] = useState({});

  // pagination states
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const paginationLimit = 10;

  useEffect(() => {
    fetchAffiliateList();
  }, [page]);

  const fetchAffiliateList = () => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_ADMIN_API_URL}affiliate/all?affiliateStatus=PENDING&page=${page}&limit=${paginationLimit}`, {
        headers: {
          "x-product-type": "PIP_GRID",
        },
      })
      .then((res) => {
        const data = res?.data?.payload?.data?.map((item, idx) => ({
          count: idx + 1,
          key: item._id,
          affiliateCode: item.affiliateCode,
          commissionType: item.commissionType,
          commissionValue: item.commissionValue,
          affiliateTotalEarning: item.affiliateTotalEarning,
          withdrawableCommission: item.withdrawableCommission,
          blockedCommission: item.blockedCommission,
          expiresAt: item.expiresAt ? dayjs(item.expiresAt).format("YYYY-MM-DD") : "",
          createdAt: item.createdAt ? dayjs(item.createdAt).format("YYYY-MM-DD") : "",
          status: item.affiliateStatus,
          user: item.user,
          socialMediaLinks: item.socialMediaLinks,
        }));
        setAffiliateList(data || []);
        setTotalCount(res?.payload?.count || 0);
        setLoading(false);
      })
      .catch((e) => {
        message.error("Failed to fetch affiliate requests");
        setLoading(false);
      });
  };

  //   const handleStatusChange = (record, newStatus) => {
  //     setCurrentLoadingId(record.key);
  //     axios
  //       .put(
  //         `${process.env.REACT_APP_ADMIN_API_URL}affiliate/update/${record.affiliateCode}`,
  //         { status: newStatus },
  //         {
  //           headers: {
  //             "x-product-type": "PIP_GRID",
  //             "Content-Type": "application/json",
  //           },
  //         }
  //       )
  //       .then((res) => {
  //         message.success(res.data?.message || `Request ${newStatus.toLowerCase()}!`);
  //         fetchAffiliateList();
  //       })
  //       .catch((e) => {
  //         message.error(e.response?.data?.message || `Failed to update status`);
  //       })
  //       .finally(() => setCurrentLoadingId(null));
  //   };

  const handleEdit = (record) => {
    setEditingAffiliate(record);
    setIsModalVisible(true);
  };

  const handleSubmit = async (values, affiliateCode = null) => {
    setModalLoading(true);
    try {
      const codeToUse = affiliateCode || editingAffiliate?.affiliateCode;
      if (!codeToUse) {
        throw new Error("No affiliate code provided");
      }

      await axios.put(`${process.env.REACT_APP_ADMIN_API_URL}affiliate/update/${codeToUse}`, values, {
        headers: {
          "x-product-type": "PIP_GRID",
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("userToken")}`,
        },
      });
      message.success(`Affiliate request ${values.status === "APPROVED" ? "approved" : "rejected"} successfully`);
      setIsModalVisible(false);
      setEditingAffiliate(null);
      fetchAffiliateList();
    } catch (error) {
      console.error("Error updating affiliate:", error);
      message.error(error.response?.data?.message || "Failed to update affiliate request");
    } finally {
      setModalLoading(false);
    }
  };

  const handleStatusChange = async (record, newStatus) => {
    if (newStatus === "REJECTED") {
      try {
        await handleSubmit({ status: "REJECTED" }, record.affiliateCode);
      } catch (error) {
        console.error("Error rejecting affiliate:", error);
      }
    } else {
      setEditingAffiliate({
        ...record,
        status: "APPROVED",
      });
      setIsModalVisible(true);
    }
  };

  const openLinksModal = (record) => {
    const label = record.user?.fullName || record.affiliateCode || "Affiliate";
    setCurrentAffiliateLabel(label);
    setCurrentLinks(record.socialMediaLinks || {});
    setIsLinksModalVisible(true);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success("Link copied to clipboard");
    } catch (e) {
      message.error("Failed to copy link");
    }
  };

  const getPlatformMeta = (key) => {
    const map = {
      facebook: { label: "Facebook", color: "#1877F2", icon: <FacebookOutlined /> },
      instagram: { label: "Instagram", color: "#E4405F", icon: <InstagramOutlined /> },
      linkedin: { label: "LinkedIn", color: "#0A66C2", icon: <LinkedinOutlined /> },
      twitter: { label: "Twitter", color: "#1DA1F2", icon: <TwitterOutlined /> },
      telegram: { label: "Telegram", color: "#0088CC", icon: <TelegramIcon /> },
    };
    return map[key] || { label: key.charAt(0).toUpperCase() + key.slice(1), color: "#595959", icon: <LinkOutlined /> };
  };

  const columns = [
    { title: "#", dataIndex: "count", key: "count", width: 50 },
    { title: "Name", dataIndex: ["user", "fullName"], key: "fullName", width: 200, render: (_, record) => record.user?.fullName || "-" },
    { title: "Email", dataIndex: ["user", "email"], key: "email", width: 220, render: (_, record) => record.user?.email || "-" },
    {
      title: "Phone Number",
      dataIndex: ["user", "phoneNumber"],
      key: "phoneNumber",
      width: 150,
      render: (_, record) => record.user?.phoneNumber || "-",
    },
    {
      title: "Social Media",
      key: "socialMedia",
      width: 150,
      render: (_, record) => {
        const hasLinks = Boolean(record.socialMediaLinks?.facebook || record.socialMediaLinks?.instagram || record.socialMediaLinks?.linkedin);
        return hasLinks ? <Button type="default" icon={<EyeOutlined />} size={"middle"} onClick={() => openLinksModal(record)} /> : <span>-</span>;
      },
    },
    { title: "Affiliate Code", dataIndex: "affiliateCode", key: "affiliateCode", width: 130 },
    {
      title: "Commission Type",
      dataIndex: "commissionType",
      key: "commissionType",
      width: 120,
      render: (text) => <Tag color={text === "percentage" ? "blue" : "green"}>{text}</Tag>,
    },
    {
      title: "Commission Value",
      dataIndex: "commissionValue",
      key: "commissionValue",
      width: 120,
      render: (val, rec) => (rec.commissionType === "percentage" ? `${val}%` : `$${val}`),
    },
    { title: "Total Earning", dataIndex: "affiliateTotalEarning", key: "affiliateTotalEarning", width: 120, render: (val) => `$${val}` },
    // { title: "Withdrawable", dataIndex: "withdrawableCommission", key: "withdrawableCommission", width: 120, render: (val) => `$${val}` },
    // { title: "Blocked", dataIndex: "blockedCommission", key: "blockedCommission", width: 120, render: (val) => `$${val}` },
    { title: "Created At", dataIndex: "createdAt", key: "createdAt", width: 140 },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status, record) => (
        <Select
          style={{ width: "120px" }}
          value={status}
          onChange={(val) => handleStatusChange(record, val)}
          disabled={status === "APPROVED" || status === "REJECTED"}
          options={[
            { value: "APPROVED", label: "Approve" },
            { value: "REJECTED", label: "Reject" },
          ]}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, record) => (
        <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Spin spinning={loading} size="large">
      <Typography.Title level={3} className="page-title">
        Affiliate Requests
      </Typography.Title>
      <Table
        columns={columns}
        dataSource={affiliateList}
        rowKey="key"
        scroll={{ x: "max-content" }}
        pagination={{
          pageSize: paginationLimit,
          current: page,
          total: totalCount,
          onChange: (p) => setPage(p),
        }}
      />
      <Modal
        title={`Social Media Links - ${currentAffiliateLabel}`}
        open={isLinksModalVisible}
        onCancel={() => setIsLinksModalVisible(false)}
        footer={null}
        width={640}
      >
        <div>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Click Open to visit or Copy to share the link.
          </Typography.Paragraph>
          <Divider style={{ margin: "8px 0 16px" }} />
          {Object.values(currentLinks || {}).filter(Boolean).length > 0 ? (
            <List
              itemLayout="horizontal"
              dataSource={Object.entries(currentLinks || {}).filter(([, url]) => Boolean(url))}
              renderItem={([platform, url]) => {
                const meta = getPlatformMeta(platform);
                return (
                  <List.Item
                    actions={[
                      <Button key="open" size="small" type="link" onClick={() => window.open(url, "_blank")}>
                        Open
                      </Button>,
                      <Button key="copy" icon={<CopyOutlined />} size="small" type="link" onClick={() => copyToClipboard(url)}>
                        Copy
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<Avatar style={{ backgroundColor: meta.color }} icon={meta.icon} />}
                      title={<span style={{ fontWeight: 600 }}>{meta.label}</span>}
                      description={<Input size="small" value={url} readOnly style={{ padding: "8px" }} />}
                    />
                  </List.Item>
                );
              }}
            />
          ) : (
            <Typography.Text type="secondary">No social media links available.</Typography.Text>
          )}
        </div>
      </Modal>
      {isModalVisible && (
        <AffiliateUpdateModal
          editingAffiliate={editingAffiliate}
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
          loading={modalLoading}
          handleSubmit={handleSubmit}
          title={editingAffiliate?.status === "APPROVED" ? "Approve Affiliate" : "Edit Affiliate"}
        />
      )}
    </Spin>
  );
};

export default AffiliateRequest;
